import React, { useState } from "react";
import { useLanguage } from "@/App";
import { 
  Bot, 
  Zap, 
  Brain, 
  MessageSquare, 
  BarChart3, 
  Workflow,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Play,
  Clock,
  TrendingUp,
  Users,
  Shield
} from "lucide-react";
import { motion } from "framer-motion";

const AIAutomation = () => {
  const { language } = useLanguage();
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: Bot,
      title_en: "AI Chatbots",
      title_nl: "AI Chatbots",
      description_en: "24/7 intelligent customer support that learns and improves",
      description_nl: "24/7 intelligente klantenservice die leert en verbetert",
      features_en: [
        "Natural language processing",
        "Multi-language support",
        "CRM integration",
        "Analytics dashboard",
        "Custom training"
      ],
      features_nl: [
        "Natuurlijke taalverwerking",
        "Meertalige ondersteuning",
        "CRM integratie",
        "Analytics dashboard",
        "Custom training"
      ],
      price: "€499",
      period_en: "/month",
      period_nl: "/maand",
      color: "from-orange-500 to-red-500",
      stats: { saves: "40+", metric_en: "hours/week", metric_nl: "uur/week" }
    },
    {
      icon: Workflow,
      title_en: "Workflow Automation",
      title_nl: "Workflow Automatisering",
      description_en: "Automate repetitive tasks and streamline operations",
      description_nl: "Automatiseer repetitieve taken en stroomlijn operaties",
      features_en: [
        "Process mapping",
        "Custom triggers",
        "API integrations",
        "Error handling",
        "Real-time monitoring"
      ],
      features_nl: [
        "Proces mapping",
        "Custom triggers",
        "API integraties",
        "Foutafhandeling",
        "Real-time monitoring"
      ],
      price: "€799",
      period_en: "/month",
      period_nl: "/maand",
      color: "from-purple-500 to-indigo-500",
      stats: { saves: "60%", metric_en: "time saved", metric_nl: "tijd bespaard" }
    },
    {
      icon: Brain,
      title_en: "AI Data Analysis",
      title_nl: "AI Data Analyse",
      description_en: "Transform raw data into actionable business insights",
      description_nl: "Transformeer ruwe data naar bruikbare business inzichten",
      features_en: [
        "Predictive analytics",
        "Custom reports",
        "Data visualization",
        "Trend detection",
        "Automated alerts"
      ],
      features_nl: [
        "Voorspellende analytics",
        "Custom rapporten",
        "Data visualisatie",
        "Trend detectie",
        "Geautomatiseerde alerts"
      ],
      price: "€999",
      period_en: "/month",
      period_nl: "/maand",
      color: "from-cyan-500 to-blue-500",
      stats: { saves: "3x", metric_en: "faster insights", metric_nl: "sneller inzicht" }
    },
    {
      icon: MessageSquare,
      title_en: "AI Content Generation",
      title_nl: "AI Content Generatie",
      description_en: "Automated content creation for marketing and communications",
      description_nl: "Geautomatiseerde content creatie voor marketing en communicatie",
      features_en: [
        "Blog articles",
        "Social media posts",
        "Email campaigns",
        "Product descriptions",
        "SEO optimization"
      ],
      features_nl: [
        "Blog artikelen",
        "Social media posts",
        "E-mail campagnes",
        "Productbeschrijvingen",
        "SEO optimalisatie"
      ],
      price: "€599",
      period_en: "/month",
      period_nl: "/maand",
      color: "from-emerald-500 to-teal-500",
      stats: { saves: "10x", metric_en: "more content", metric_nl: "meer content" }
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title_en: "Save Time",
      title_nl: "Bespaar Tijd",
      description_en: "Automate hours of manual work every day",
      description_nl: "Automatiseer uren handmatig werk per dag"
    },
    {
      icon: TrendingUp,
      title_en: "Scale Faster",
      title_nl: "Schaal Sneller",
      description_en: "Grow without proportionally increasing costs",
      description_nl: "Groei zonder proportioneel kosten te verhogen"
    },
    {
      icon: Users,
      title_en: "Better CX",
      title_nl: "Betere CX",
      description_en: "24/7 instant responses for your customers",
      description_nl: "24/7 directe reacties voor je klanten"
    },
    {
      icon: Shield,
      title_en: "Reduce Errors",
      title_nl: "Minder Fouten",
      description_en: "AI precision eliminates human mistakes",
      description_nl: "AI precisie elimineert menselijke fouten"
    }
  ];

  const getTitle = (item) => language === "nl" ? item.title_nl : item.title_en;
  const getDescription = (item) => language === "nl" ? item.description_nl : item.description_en;
  const getFeatures = (item) => language === "nl" ? item.features_nl : item.features_en;
  const getPeriod = (item) => language === "nl" ? item.period_nl : item.period_en;
  const getMetric = (item) => language === "nl" ? item.stats.metric_nl : item.stats.metric_en;

  return (
    <section
      id="ai-automation"
      data-testid="ai-automation-section"
      className="py-32 bg-gradient-to-b from-[#030303] via-[#0a0505] to-[#030303] relative overflow-hidden"
    >
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,107,0,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.08),transparent_50%)]" />
        
        {/* Animated grid */}
        <motion.div 
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,107,0,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,107,0,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Floating orbs */}
        <motion.div
          animate={{ 
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px]"
        />
        <motion.div
          animate={{ 
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-[80px]"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-orange-500/20 px-5 py-2.5 mb-6"
          >
            <Sparkles size={16} className="text-orange-400" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-purple-400 text-sm font-bold uppercase tracking-[0.2em]">
              {language === "nl" ? "Transformeer Je Business" : "Transform Your Business"}
            </span>
          </motion.div>

          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            AI{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] via-purple-500 to-[#FF6B00]">
              Automation
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl" 
              ? "Laat AI het zware werk doen. Onze automatiseringsoplossingen besparen tijd, verlagen kosten en verhogen efficiëntie."
              : "Let AI do the heavy lifting. Our automation solutions save time, reduce costs, and increase efficiency."}
          </p>
        </motion.div>

        {/* Benefits Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/[0.02] border border-white/5 p-6 text-center hover:border-orange-500/30 transition-all group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <benefit.icon size={24} className="text-orange-400" />
              </div>
              <h3 className="text-white font-bold mb-1">{getTitle(benefit)}</h3>
              <p className="text-zinc-500 text-sm">{getDescription(benefit)}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Service selector */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {language === "nl" ? "Onze AI Oplossingen" : "Our AI Solutions"}
            </h3>
            
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setActiveService(index)}
                className={`relative p-6 cursor-pointer transition-all ${
                  activeService === index 
                    ? 'bg-gradient-to-r from-orange-500/10 to-transparent border-l-2 border-orange-500' 
                    : 'bg-white/[0.02] border-l-2 border-transparent hover:bg-white/[0.04]'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <service.icon size={24} className="text-white" />
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-white font-bold">{getTitle(service)}</h4>
                      <div className="text-right">
                        <span className="text-orange-400 font-black text-xl">{service.price}</span>
                        <span className="text-zinc-500 text-sm">{getPeriod(service)}</span>
                      </div>
                    </div>
                    <p className="text-zinc-400 text-sm">{getDescription(service)}</p>
                    
                    {/* Stats badge */}
                    <div className="inline-flex items-center gap-2 mt-3 bg-white/5 px-3 py-1">
                      <Zap size={12} className="text-orange-400" />
                      <span className="text-orange-400 font-bold text-sm">{service.stats.saves}</span>
                      <span className="text-zinc-500 text-xs">{getMetric(service)}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Active service detail */}
          <motion.div
            key={activeService}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            {/* Glow */}
            <div className={`absolute -inset-2 bg-gradient-to-r ${services[activeService].color} opacity-20 blur-2xl`} />
            
            <div className="relative bg-[#0A0A0A] border border-white/10 p-8 h-full">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className={`w-16 h-16 bg-gradient-to-br ${services[activeService].color} rounded-xl flex items-center justify-center`}>
                  {React.createElement(services[activeService].icon, { size: 32, className: "text-white" })}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {getTitle(services[activeService])}
                  </h3>
                  <p className="text-zinc-400">{getDescription(services[activeService])}</p>
                </div>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h4 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-4">
                  {language === "nl" ? "Inclusief" : "Includes"}
                </h4>
                <ul className="space-y-3">
                  {getFeatures(services[activeService]).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle size={18} className="text-orange-400 flex-shrink-0" />
                      <span className="text-white">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price & CTA */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-end justify-between mb-6">
                  <div>
                    <span className="text-zinc-500 text-sm">{language === "nl" ? "Vanaf" : "Starting at"}</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black text-white">{services[activeService].price}</span>
                      <span className="text-zinc-400">{getPeriod(services[activeService])}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-orange-400">
                      <Zap size={16} />
                      <span className="font-bold">{services[activeService].stats.saves}</span>
                    </div>
                    <span className="text-zinc-500 text-sm">{getMetric(services[activeService])}</span>
                  </div>
                </div>
                
                <a
                  href="#contact"
                  className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white py-4 font-bold hover:shadow-[0_0_30px_rgba(255,107,0,0.4)] transition-all"
                >
                  {language === "nl" ? "Start Nu" : "Get Started"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Custom Solution CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-purple-500/10 to-orange-500/10 blur-xl" />
          <div className="relative bg-[#0A0A0A] border border-white/10 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Brain size={40} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {language === "nl" ? "Custom AI Oplossing Nodig?" : "Need a Custom AI Solution?"}
                  </h3>
                  <p className="text-zinc-400">
                    {language === "nl" 
                      ? "Wij bouwen op maat gemaakte AI systemen voor jouw specifieke business uitdagingen."
                      : "We build custom AI systems tailored to your specific business challenges."}
                  </p>
                </div>
              </div>
              <a
                href="#contact"
                className="flex-shrink-0 group inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-bold hover:bg-orange-400 transition-all"
              >
                {language === "nl" ? "Plan een Gesprek" : "Schedule a Call"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIAutomation;
